import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarClock,
  FileText,
  GitBranch,
  GitPullRequest,
  RefreshCw,
  Rocket,
  RotateCcw,
  SearchX,
  Send,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../api";
import { Markdown } from "../components/issues/markdown";
import { LabelChip, StatusDisc, useIssuesMeta } from "../components/issues/meta";
import { EmptyState, PageSkeleton, Stat } from "../components/page";
import { ReleaseChangesCard } from "../components/releases/release-changes";
import { ShipReleaseDialog } from "../components/releases/release-dialogs";
import {
  formatSchedule,
  RELEASE_STATUS_LABEL,
  ReleaseKindBadge,
  ReleaseStatusBadge,
  releaseTitle,
} from "../components/releases/release-meta";
import { ReleaseTimelineItem, releaseEventActor } from "../components/releases/release-timeline";
import { StatusBadge } from "../components/status-badge";
import { TimeAgo } from "../components/time-ago";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { actorName } from "../lib/people";
import { useIsAdmin } from "../lib/permissions";
import { asSentence } from "../lib/tasks";
import { type IssuesMeta, type IssueView, type MergeStatus, OPEN_RELEASE } from "../types";

const STEPS = ["open", "shipping", "published", "deploying", "deployed"] as const;

export function ReleaseDetailPage() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const admin = useIsAdmin();
  const meta = useIssuesMeta();
  const query = useQuery({
    queryKey: ["release", id],
    queryFn: () => api.release(id),
    refetchInterval: 10_000,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["release", id] });
    void queryClient.invalidateQueries({ queryKey: ["releases"] });
    void queryClient.invalidateQueries({ queryKey: ["issues"] });
  };
  const fail = (fallback: string) => (error: unknown) =>
    toast.error(error instanceof ApiError ? error.message : fallback);
  const [shipOpen, setShipOpen] = useState(false);
  const ship = useMutation({
    mutationFn: (tag: string | undefined) => api.shipRelease(id, tag),
    onSuccess: (detail) => {
      toast.success(
        detail.release.status === "shipping"
          ? "Shipping: publishing once every issue is done"
          : `Published ${detail.release.tag ?? detail.release.name}`,
      );
      setShipOpen(false);
      refresh();
    },
    onError: fail("Could not ship the release"),
  });
  const cancel = useMutation({
    mutationFn: () => api.cancelRelease(id),
    onSuccess: () => {
      toast.success("Release cancelled; its issues rejoin the next release when done");
      refresh();
    },
    onError: fail("Could not cancel the release"),
  });
  const retry = useMutation({
    mutationFn: () => api.retryRelease(id),
    onSuccess: () => {
      toast.success("Publishing again");
      refresh();
    },
    onError: fail("Retry failed"),
  });
  const finish = useMutation({
    mutationFn: () => api.retryRelease(id),
    onSuccess: () => {
      toast.success("Finishing the release");
      refresh();
    },
    onError: fail("Could not finish the release"),
  });
  const sync = useMutation({
    mutationFn: () => api.syncRelease(id),
    onSuccess: () => {
      toast.success("Synced from GitHub");
      refresh();
    },
    onError: fail("Sync failed"),
  });
  const regenerate = useMutation({
    mutationFn: () => api.regenerateReleaseNotes(id),
    onSuccess: () => {
      toast.success("Release notes regenerated from GitHub");
      refresh();
    },
    onError: fail("Could not regenerate the release notes"),
  });
  const notify = useMutation({
    mutationFn: () => api.notifyReleaseTeams(id),
    onSuccess: (detail) => {
      const teams = ([...detail.events].reverse().find((event) => event.type === "notices_resent")?.payload
        .teams ?? []) as string[];
      toast.success(
        teams.length ? `Resent the Slack summary to ${teams.join(", ")}` : "Resent the Slack summary",
      );
      refresh();
    },
    onError: fail("Could not resend the Slack summary"),
  });

  if (query.isLoading) return <PageSkeleton />;
  const detail = query.data;
  if (!detail) {
    return (
      <EmptyState icon={SearchX} title="Release not found" description="It may have been removed.">
        <Button variant="outline" asChild>
          <Link to="/releases">Back to releases</Link>
        </Button>
      </EmptyState>
    );
  }
  const { release, issues, merges, events } = detail;
  const shipperName =
    releaseEventActor([...events].reverse().find((event) => event.type === "ship_requested")) ??
    releaseEventActor([...events].reverse().find((event) => event.type === "published_in_github")) ??
    actorName(release.shippedBy);
  const open = OPEN_RELEASE.has(release.status);
  const hotfixBranch = release.branch.startsWith("release/");
  const stepIndex = STEPS.indexOf(release.status as (typeof STEPS)[number]);
  const held = issues.filter((issue) => merges[issue.id]?.status === "held");
  const notDone = issues.filter((issue) => issue.statusKey !== "done");

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/releases">Releases</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{releaseTitle(release)}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <ReleaseKindBadge kind={release.kind} />
            <ReleaseStatusBadge status={release.status} />
            {release.source === "github" && (
              <span className="text-xs text-muted-foreground">created in GitHub</span>
            )}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {release.name}
            {release.tag && release.tag !== release.name && (
              <span className="ml-2 font-mono text-lg text-muted-foreground">{release.tag}</span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            {release.kind === "scheduled" && release.scheduledFor && (
              <>
                <CalendarClock className="inline size-3.5" /> ships {formatSchedule(release.scheduledFor)} ·{" "}
              </>
            )}
            created <TimeAgo at={release.createdAt} />
            {actorName(release.createdBy) ? ` by ${actorName(release.createdBy)}` : ""}
            {release.shippedAt ? (
              <>
                {" "}
                · shipped <TimeAgo at={release.shippedAt} />
              </>
            ) : null}
            {shipperName ? ` by ${shipperName}` : ""}
          </p>
          {release.error && (
            <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="size-3.5" /> {release.error}
            </p>
          )}
          {release.finishError && (
            <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
              <AlertTriangle className="size-3.5" /> {release.finishError}. Skynet retries on its own.
            </p>
          )}
          {release.notesError && (
            <p className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="size-3.5" /> {release.notesError}. The notes list only Skynet's issues
              until they are regenerated.
            </p>
          )}
          {release.syncConflict && (
            <p className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400">
              <AlertTriangle className="size-3.5" /> {release.branch} conflicts with main. Merge it back by
              hand.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {release.githubUrl && (
            <Button variant="outline" asChild>
              <a href={release.githubUrl} target="_blank" rel="noreferrer">
                {release.draft ? "Draft in GitHub" : "Release in GitHub"}{" "}
                <ArrowUpRight className="size-3.5" />
              </a>
            </Button>
          )}
          {release.deployUrl && (
            <Button variant="outline" asChild>
              <a href={release.deployUrl} target="_blank" rel="noreferrer">
                Deploy run <ArrowUpRight className="size-3.5" />
              </a>
            </Button>
          )}
          {admin && release.githubReleaseId != null && (
            <Button variant="ghost" disabled={sync.isPending} onClick={() => sync.mutate()}>
              <RefreshCw /> Sync
            </Button>
          )}
          {release.publishedAt && release.githubReleaseId != null && (
            <Button
              variant={release.notesError ? "outline" : "ghost"}
              disabled={regenerate.isPending}
              onClick={() => regenerate.mutate()}
            >
              <FileText /> Regenerate notes
            </Button>
          )}
          {release.publishedAt && (
            <Button variant="outline" disabled={notify.isPending} onClick={() => notify.mutate()}>
              <Send /> Resend Slack summary
            </Button>
          )}
          {admin && release.finishError && (
            <Button variant="outline" disabled={finish.isPending} onClick={() => finish.mutate()}>
              <RotateCcw /> Finish shipping
            </Button>
          )}
          {admin && release.status === "failed" && !release.publishedAt && (
            <Button variant="outline" disabled={retry.isPending} onClick={() => retry.mutate()}>
              <RotateCcw /> Retry publish
            </Button>
          )}
          {admin && open && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={cancel.isPending}>
                  <XCircle /> Cancel
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel {release.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The GitHub draft is deleted, held pull requests are released to the Merge agent, and the
                    issues rejoin the next standard release when they are done.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep it</AlertDialogCancel>
                  <AlertDialogAction onClick={() => cancel.mutate()}>Cancel release</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {admin && release.status === "open" && (
            <Button disabled={release.issueCount === 0} onClick={() => setShipOpen(true)}>
              <Rocket /> Ship now
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <ol className="grid grid-cols-5 gap-2 text-xs">
            {STEPS.map((step, index) => {
              const reached = stepIndex >= index || release.status === "deployed";
              const failedHere =
                release.status === "failed" && (release.publishedAt ? index === 3 : index === 2);
              return (
                <li key={step} className="space-y-1.5">
                  <div
                    className={
                      failedHere
                        ? "h-1.5 rounded-full bg-red-500"
                        : reached
                          ? "h-1.5 rounded-full bg-primary"
                          : "h-1.5 rounded-full bg-muted"
                    }
                  />
                  <span className={reached || failedHere ? "font-medium" : "text-muted-foreground"}>
                    {RELEASE_STATUS_LABEL[step]}
                  </span>
                </li>
              );
            })}
          </ol>
          <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Tag"
              value={<span className="font-mono">{release.tag ?? detail.suggestedTag ?? "—"}</span>}
            />
            <Stat
              label="Branch"
              value={
                <span className="inline-flex items-center gap-1 font-mono">
                  {hotfixBranch && <GitBranch className="size-3" />}
                  {release.branch}
                  {release.baseTag ? (
                    <span className="text-muted-foreground"> from {release.baseTag}</span>
                  ) : null}
                </span>
              }
            />
            <Stat
              label="Target"
              value={
                <span className="font-mono">{release.targetSha ? release.targetSha.slice(0, 7) : "—"}</span>
              }
            />
            <Stat
              label="Deploy"
              value={
                release.deployedAt ? (
                  <>
                    deployed <TimeAgo at={release.deployedAt} />
                  </>
                ) : release.deployStartedAt ? (
                  <>
                    running since <TimeAgo at={release.deployStartedAt} />
                  </>
                ) : release.publishedAt ? (
                  "waiting for the workflow"
                ) : (
                  "—"
                )
              }
            />
          </dl>
          {detail.latest && open && (
            <p className="mt-3 text-xs text-muted-foreground">
              Latest published in GitHub:{" "}
              <a
                href={detail.latest.url}
                target="_blank"
                rel="noreferrer"
                className="font-mono hover:underline"
              >
                {detail.latest.tag}
              </a>
              {detail.suggestedTag ? ` · this release would publish as ${detail.suggestedTag}` : ""}
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="gap-0 py-0">
        <CardHeader className="border-b px-4 py-3">
          <CardTitle className="flex items-baseline justify-between gap-3 text-sm">
            Issues
            <span className="font-normal text-muted-foreground tabular-nums">{issues.length}</span>
          </CardTitle>
          {release.status === "shipping" && notDone.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Waiting for {notDone.map((issue) => issue.identifier).join(", ")} to merge.
            </p>
          )}
          {release.status === "open" && held.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {held.length} pull request{held.length === 1 ? "" : "s"} held until this ships.
            </p>
          )}
        </CardHeader>
        <CardContent className="px-0">
          {issues.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              No issues yet. Done issues join the next standard release on their own; promote one from its
              sidebar for an urgent or scheduled release.
            </p>
          ) : (
            <ul className="divide-y">
              {issues.map((issue) => (
                <ReleaseIssueRow key={issue.id} issue={issue} meta={meta.data} merge={merges[issue.id]} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {detail.changes && <ReleaseChangesCard releaseId={release.id} changes={detail.changes} open={open} />}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <p className="text-sm text-muted-foreground">No events yet.</p>
            ) : (
              <ul className="space-y-2">
                {[...events].reverse().map((event) => (
                  <ReleaseTimelineItem key={event.seq} event={event} />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Release notes</CardTitle>
          </CardHeader>
          <CardContent>
            {release.body.trim() ? (
              <Markdown className="text-sm">{release.body}</Markdown>
            ) : (
              <p className="text-sm text-muted-foreground">Empty.</p>
            )}
            {release.githubUrl && (
              <p className="mt-3 text-xs text-muted-foreground">
                Notes are edited in GitHub. Skynet keeps the issue list at the top up to date, and when the
                release publishes it adds the changes Skynet did not build and GitHub's generated notes,
                keeping anything written in between.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <ShipReleaseDialog
        open={shipOpen}
        onOpenChange={setShipOpen}
        release={release}
        suggestedTag={detail.suggestedTag}
        pending={ship.isPending}
        onConfirm={(tag) => ship.mutate(tag)}
      />
    </>
  );
}

function pullRequestLabel(url: string): string {
  const match = url.match(/\/pull\/(\d+)/);
  return match ? `#${match[1]}` : "PR";
}

/** One line per issue: what it is, where it stands, and the pull request that carries it. */
function ReleaseIssueRow({
  issue,
  meta,
  merge,
}: {
  issue: IssueView;
  meta: IssuesMeta | undefined;
  merge?: { status: MergeStatus; prUrl: string; blockers: string[] };
}) {
  const status = meta?.statuses.find((entry) => entry.key === issue.statusKey);
  const prUrl = merge?.prUrl ?? issue.prUrl;
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40">
      <Link
        to={`/issues/${issue.identifier}`}
        className="w-17.5 shrink-0 font-mono text-xs whitespace-nowrap text-muted-foreground hover:text-foreground hover:underline"
      >
        {issue.identifier}
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <Link to={`/issues/${issue.identifier}`} className="truncate text-sm font-medium hover:underline">
            {issue.title}
          </Link>
          {issue.labels.slice(0, 2).map((label) => (
            <LabelChip
              key={label.id}
              name={label.name}
              color={label.color}
              className="hidden shrink-0 sm:inline-flex"
            />
          ))}
        </div>
        {merge && merge.blockers.length > 0 && (
          <p className="mt-0.5 truncate text-xs text-amber-600 dark:text-amber-400">
            {merge.blockers.map(asSentence).join(" · ")}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:inline-flex">
          <StatusDisc category={status?.category} color={status?.color} />
          {status?.name ?? issue.statusKey}
        </span>
        {merge && <StatusBadge status={merge.status} />}
        {prUrl && (
          <a
            href={prUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            <GitPullRequest className="size-3.5" />
            {pullRequestLabel(prUrl)}
          </a>
        )}
        <span className="w-14 text-right text-xs text-muted-foreground tabular-nums">
          <TimeAgo at={issue.updatedAt} />
        </span>
      </div>
    </li>
  );
}
